import { useState, useEffect } from 'react';
import { Settings, Save, Instagram } from 'lucide-react';
import toast from 'react-hot-toast';
import { settingService } from '../../services/settingService';
import { UI, ui, buttonStyle, fieldStyles } from '../../utils/ui';

const ManageSettings = () => {
  const [settings, setSettings] = useState([]);
  const [values, setValues]     = useState({});
  const [saving, setSaving]     = useState({});
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    settingService.getSettings()
      .then(res => {
        setSettings(res.data.settings || []);
        const init = {};
        (res.data.settings || []).forEach(s => { init[s.key] = s.value; });
        setValues(init);
      })
      .catch(() => toast.error('Failed to load settings.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await settingService.updateSetting(key, values[key]);
      toast.success('Setting saved.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save setting.');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const settingMeta = {
    hold_duration_minutes: {
      label:       'Room Hold Duration (minutes)',
      description: 'How long a room is held for a user after they proceed to the confirmation step. After this time the hold expires and the room becomes available again.',
      type:        'number',
      min:         1,
      max:         1440,
    },
    social_instagram: {
      label:       'Instagram URL',
      description: 'Full Instagram profile URL shown in the site footer.',
      type:        'url',
    },
    social_whatsapp: {
      label:       'WhatsApp URL',
      description: 'WhatsApp link shown in the site footer (e.g. https://wa.me/91XXXXXXXXXX).',
      type:        'url',
    },
    social_facebook: {
      label:       'Facebook URL',
      description: 'Facebook page URL shown in the site footer. Leave empty to hide the icon.',
      type:        'url',
    },
  };

  return (
    <div style={{ padding: '32px', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>
          Portal Settings
        </h1>
        <p style={{ fontSize: 15, color: '#6b7280', margin: 0 }}>Configure system-wide behaviour for the portal.</p>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <div style={{ width: 40, height: 40, border: '4px solid #dcfce7', borderTop: '4px solid #16a34a', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {Object.entries(settingMeta).map(([key, meta]) => (
            <div key={key} style={{ ...ui.card, padding: 28 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Settings size={20} color="#16a34a" />
                </div>
                <div>
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>
                    {meta.label}
                  </h2>
                  <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>{meta.description}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <label style={fieldStyles.label}>Value</label>
                  <input
                    type={meta.type === 'url' ? 'text' : (meta.type || 'text')}
                    min={meta.min}
                    max={meta.max}
                    placeholder={meta.placeholder || ''}
                    value={values[key] ?? ''}
                    onChange={e => setValues(prev => ({ ...prev, [key]: e.target.value }))}
                    style={{ ...fieldStyles.input, maxWidth: meta.type === 'url' ? 480 : 220 }}
                  />
                </div>
                <button
                  onClick={() => handleSave(key)}
                  disabled={saving[key]}
                  style={{ ...buttonStyle('primary'), padding: '10px 22px', borderRadius: 10, opacity: saving[key] ? 0.75 : 1, display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
                >
                  <Save size={16} />
                  {saving[key] ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManageSettings;
